import { FC, SetStateAction, useEffect, useState } from 'react';
import ModalComponent from '../modal/modal';
import {
  renderModalTitle,
  renderViewUser,
  renderCreationForm,
  renderFormRow,
  countries,
} from './helper';
import { isUndefined } from 'lodash';
import { PinOptionType, UserModalProps, UserOperation } from './type';
import { Trans, useTranslation } from 'react-i18next';
import { getUserKebabOptions } from '../../pages/userManagement/userManagementHelper';
import { Option } from '../dropDownInput/dropDownInput.type';
import { CreateUserResponse, UserStatus } from '@inplay/models';
import {
  FieldConfig,
  UserPresentationStrategy,
} from '../../strategies/userPresentation/userPresentationStrategy.interface';
import { UserPresentationFactory } from '../../strategies/userPresentation/userPresentationFactory';
import {
  validateAndDecodeAuthToken,
  getChildOrganizationUnitOptions,
  convertFileToBase64,
} from '../../utils/helper';
import { OrganizationUnitType, UserType } from '@inplay/models';
import { AuthTokenResponse } from '../../service/service.type';
import { useUserDetailContext } from '../../service/context/userDetailContext';
import { useOrganizationContext } from '../../service/context/organizationContext';
import {
  useCreateUser,
  useResendInvitationEmail,
  useUpdateUser,
  useUsersSummary,
} from '../../hooks/useUserData';
import {
  apiStatusCode,
  CreateUserStatusResponse,
} from '../../utils/helper.type';
import InviteButton from '../../component/button/button';
import Switch from '../switch/switch';
import Loading from '../../assets/loading/loading';

export const UserModal: FC<UserModalProps> = (props) => {
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [userDetails, setUserDetails] = useState<Record<string, any>>({});
  const [errorMessage, setErrorMessage] = useState<Record<string, string>>({});
  const [openMenuRowIndex, setOpenMenuRowIndex] = useState<number | null>(null);
  const [createFieldConfigs, setCreateFieldConfigs] = useState<FieldConfig[]>(
    [],
  );

  const { t } = useTranslation();

  const { fetchUsersData } = useUsersSummary();

  useEffect(() => {
    if (props.selectedUser?.status === UserStatus.ACTIVE) {
      props.setActiveLabel(true);
    } else {
      props.setActiveLabel(false);
    }
  }, [props.selectedUser]);

  const { invitationEmail, invitationEmailLoading } =
    useResendInvitationEmail();

  const { createUser, createUserLoading } = useCreateUser();

  const { updateUser, updateUserLoading } = useUpdateUser();

  const inviteUpdateLoading = updateUserLoading || invitationEmailLoading;

  const { userDetail } = useUserDetailContext();
  const { organizationSummary } = useOrganizationContext();

  const isAllPhotoFieldsNull: boolean =
    formValues.photo &&
    Object.values(formValues.photo).every((value) => value === null);

  useEffect(() => {
    console.log('UserModal useEffect triggered', {
      operation: props.operation,
      isOpen: props.isOpenUserModal,
    });
    const validAuthToken = validateAndDecodeAuthToken();
    const userType = validAuthToken?.type as UserType;
    if (isUndefined(userDetail) || isUndefined(organizationSummary)) {
      console.error(
        'User detail is undefined. Cannot proceed with user modal.',
      );
      return;
    } else if (
      props.operation === UserOperation.VIEW &&
      !isUndefined(props.selectedUser)
    ) {
      const strategy = UserPresentationFactory.getPresentationStrategy(
        userType,
        userDetail,
      );
      const userDetails: Record<string, string> =
        strategy.getViewableFieldsOfUser(props.selectedUser);
      setUserDetails(userDetails);
      props.setActiveLabel(props.selectedUser?.status !== UserStatus.INACTIVE);
    } else if (
      props.operation === UserOperation.ADD ||
      props.operation === UserOperation.EDIT
    ) {
      // Get the current user type from auth token
      console.log('Initializing user creation/modification modal...');

      // Get the appropriate strategy for the current user
      const strategy = UserPresentationFactory.getPresentationStrategy(
        userType,
        userDetail,
      );
      // Get field configurations for user creation
      const createFieldConfigs: FieldConfig[] =
        props.operation === UserOperation.EDIT && props.selectedUser
          ? strategy.getEditableFieldsOfUser(
              props.selectedUser,
              organizationSummary,
            )
          : strategy.getCreationFieldsConfig(
              UserType.VENUE_STAFF,
              organizationSummary,
            );

      setCreateFieldConfigs(createFieldConfigs);

      console.log(
        'Field configs:',
        createFieldConfigs.map((c) => `${c.id}: ${c.defaultValue}`),
      );

      // Initialize form values with default values
      const initialValues = createFieldConfigs.reduce(
        (defaultConfig: Record<string, any>, config: FieldConfig) => {
          if (config.type === 'phone') {
            defaultConfig[config.id] = {
              number: config.defaultValue,
              country: countries[0],
            };
          } else if (config.type === 'photo') {
            defaultConfig[config.id] = {
              profilePicture: '',
              profilePictureUrl: config.defaultValue || '',
              profilePictureBase64: '',
              fileName: '',
            };
          } else if (config.defaultValue !== undefined) {
            defaultConfig[config.id] = config.defaultValue;
          }
          return defaultConfig;
        },
        {} as Record<string, any>,
      );

      console.log('Initial values:', initialValues);

      // Set default userType only if not already defined in initialValues
      const formValues: Record<string, any> = {
        ...initialValues,
        userType: initialValues.userType || UserType.VENUE_STAFF,
      };

      // Ensure all fields with defaultValue are set in formValues
      createFieldConfigs.forEach((config) => {
        if (
          !isUndefined(config.defaultValue) &&
          isUndefined(formValues[config.id])
        ) {
          formValues[config.id] = config.defaultValue;
        }
      });

      setFormValues(formValues);
    }
  }, [props.isOpenUserModal, props.operation, props.selectedUser]);

  const validateForm = () => {
    const errors: Record<string, string> = {};

    // Validate each field using its validation function
    createFieldConfigs.forEach((createFieldConfig: FieldConfig) => {
      if (createFieldConfig.required && !formValues[createFieldConfig.id]) {
        errors[createFieldConfig.id] = `${createFieldConfig.label} is required`; //TODO: Khushbu to implement i18n for error messages
      } else if (
        createFieldConfig.validation &&
        formValues[createFieldConfig.id]
      ) {
        const error: string | null = createFieldConfig.validation(
          formValues[createFieldConfig.id],
        );
        if (error) {
          errors[createFieldConfig.id] = error;
        }
      }
    });

    const pinField = formValues['pin'];

    if (pinField?.option === PinOptionType.CUSTOM && pinField.pin) {
      if (!/^\d{4}$/.test(pinField.pin)) {
        errors['pin'] = t('userManagement.pinValidation');
      }
    }

    setErrorMessage(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Updates the options for a child field based on the selected parent value
   *
   * @param parentField - The field ID of the parent (e.g., 'venue', 'subOrganization')
   * @param parentValue - The selected value of the parent field
   * @param childField - The field ID of the child (e.g., 'venueBranch', 'venue')
   * @param childType - The OrganizationUnitType of the child field
   * @param resetChildValue - Whether to reset the child field value (default: true)
   */
  const updateChildFieldOptions = (
    parentOrganizationUnitType: OrganizationUnitType,
    parentOrganizationUnitId: string,
    childField: string,
    childType: OrganizationUnitType,
    resetChildValue: boolean = true,
  ) => {
    if (isUndefined(organizationSummary)) {
      console.error(
        'Organization summary is undefined. Cannot update child field options.',
      );
      return;
    }
    console.log({
      parentOrganizationUnitType,
      parentOrganizationUnitId,
      childField,
      childType,
      resetChildValue,
    });
    // Find the child field config
    const childFieldConfig = createFieldConfigs.find(
      (config) => config.id === childField,
    );

    if (!childFieldConfig) {
      console.warn(`Child field ${childField} not found in field configs.`);
      return;
    }
    // Get options for the child field based on parent value
    const childOptions = getChildOrganizationUnitOptions(
      organizationSummary,
      parentOrganizationUnitId,
    );

    // Create a new config with updated options
    const updatedConfig = {
      ...childFieldConfig,
      options: childOptions,
    };

    // Update the field configs with the new child field config
    const updatedConfigs = createFieldConfigs.map((config) =>
      config.id === childField ? updatedConfig : config,
    );

    setCreateFieldConfigs(updatedConfigs);

    // Reset child field value if needed
    if (resetChildValue) {
      setFormValues((prevValues) => ({
        ...prevValues,
        [childField]: Array.isArray(prevValues[childField]) ? [] : '',
      }));
    }
  };

  const handleChange = (field: string, value: any) => {
    if (isUndefined(userDetail) || isUndefined(organizationSummary)) {
      console.error('User detail is undefined. Cannot change field value.');
      return;
    }

    console.log(`Changing field ${field} to:`, value);

    const currentFieldValue = formValues[field];

    // Handle partial updates for nested structures like photo
    let newFieldValue: any;
    if (
      typeof value === 'object' &&
      value !== null &&
      typeof currentFieldValue === 'object' &&
      !Array.isArray(value) &&
      !Array.isArray(currentFieldValue)
    ) {
      newFieldValue = { ...currentFieldValue, ...value }; // Merge
    } else {
      newFieldValue = value; // Replace
    }

    const newValues = {
      ...formValues,
      [field]: newFieldValue,
    };

    // Handle parent-child field relationships
    if (field === 'venue') {
      console.log('Updating venue field options');
      const parentOrganizationUnitType: OrganizationUnitType =
        OrganizationUnitType.VENUE;
      updateChildFieldOptions(
        parentOrganizationUnitType,
        value,
        'venueBranch',
        OrganizationUnitType.VENUE_BRANCH,
      );
    } else if (field === 'subOrganizations') {
      console.log('Updating subOrganizations field options');
      const parentOrganizationUnitType: OrganizationUnitType =
        OrganizationUnitType.SUB_ORGANIZATION;
      updateChildFieldOptions(
        parentOrganizationUnitType,
        value,
        'venue',
        OrganizationUnitType.VENUE,
      );
    }

    setFormValues(newValues);

    // If userType changed, update field configurations
    if (field === 'userType') {
      const validAuthToken: AuthTokenResponse | null =
        validateAndDecodeAuthToken();
      const currentUserType: UserType = validAuthToken?.type as UserType;
      const strategy: UserPresentationStrategy =
        UserPresentationFactory.getPresentationStrategy(
          currentUserType,
          userDetail,
        );

      const updatedConfigs: FieldConfig[] = strategy.getCreationFieldsConfig(
        value as UserType,
        organizationSummary,
      );

      const updatedValues = { ...newValues };
      updatedConfigs.forEach((config) => {
        if (
          config.defaultValue !== undefined &&
          updatedValues[config.id] === undefined
        ) {
          updatedValues[config.id] = config.defaultValue;
        }
      });

      setCreateFieldConfigs(updatedConfigs);
      setFormValues(updatedValues);
    }

    // Clear error for this field
    if (errorMessage[field]) {
      setErrorMessage((previous: SetStateAction<Record<string, string>>) => ({
        ...previous,
        [field]: '',
      }));
    }
  };

  const handleInvitationEmail = async (email: string) => {
    if (invitationEmailLoading) {
      console.log(
        '[EmailInvitation] Already in progress... skipping this request',
      );
      return;
    }
    try {
      console.log(`[ENTRY][EmailInvitation]`);
      await invitationEmail(email);
      console.log(
        `[EmailInvitation][Close loading and receive success toast message].......`,
      );
    } catch (error: unknown) {
      console.warn(`[EmailInvitation]`, { error });
      throw error;
    }
  };
  const onApply = async () => {
    if (createUserLoading) {
      console.log('[CreateUser] Already in progress... skipping this request');
      return;
    }
    if (validateForm()) {
      try {
        console.log(`[ENTRY][CreateUser]`);
        const createUserKeyResponse: CreateUserResponse | null =
          await createUser({
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            email: formValues.email,
            profilePhoto: formValues.photo.profilePictureBase64,
            organizationId: formValues.organization,
            subOrganizationId: formValues.subOrganizations,
            subOrganizationIds: formValues.subOrganizationIds,
            venueId: formValues.venue,
            venueBranchId: formValues.venueBranch,
            venueBranchIds: formValues.venueBranch,
            mobileCountryCode: formValues?.mobile?.country?.dialCode,
            mobileNumber: formValues?.mobile?.number,
            pin: formValues?.pin?.pin,
            pinOption: formValues?.pin?.option,
            type: formValues.userType,
            fetchUsersData,
          });
        if (!isUndefined(createUserKeyResponse)) {
          const createUserResponse: CreateUserStatusResponse | null =
            createUserKeyResponse && {
              statusCode: apiStatusCode.SUCCESS,
              data: createUserKeyResponse,
            };

          console.log(`[CreateUser] response `, createUserResponse);
          if (createUserResponse?.statusCode === apiStatusCode.SUCCESS) {
            console.log(`[CreateUser] close modal `);

            props.handleCloseUserModal();
          }
        }
      } catch (error: unknown) {
        console.warn(`[ActivateDeActivateUser]`, { error });
        throw error;
      } finally {
        console.warn(`[ActivateDeActivateUser] finally block...`);
      }
      console.log('Form submitted with values:', formValues);
    }
  };

  const onEdit = async () => {
    if (updateUserLoading) {
      console.log('[UpdateUser] Already in progress... skipping this request');
      return;
    }
    const userId: string | undefined = props.selectedUser?.id;
    if (!userId) {
      console.error('User ID is undefined. Cannot update user.');
      return;
    }

    if (validateForm()) {
      try {
        //  Send invitation email only if status is INVITED
        if (props.selectedUser?.status === UserStatus.INVITED) {
          console.log(`[ENTRY][SendInvitationEmail]`);
          await handleInvitationEmail(formValues.email);
        }

        console.log(`[ENTRY][UpdateUser]`);

        const updateUserKeyResponse: CreateUserResponse | null =
          await updateUser({
            userId,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            email: formValues.email,
            ...(formValues.photo &&
              (isAllPhotoFieldsNull
                ? { profilePhoto: '' }
                : formValues.photo.profilePictureBase64 && {
                    profilePhoto: formValues.photo.profilePictureBase64,
                  })),
            organizationId: formValues.organization,
            subOrganizationId: formValues.subOrganizations,
            subOrganizationIds: formValues.subOrganizationIds,
            venueId: formValues.venue,
            venueBranchId: formValues.venueBranch,
            venueBranchIds: formValues.venueBranch,
            mobileCountryCode: formValues?.mobile?.country?.dialCode,
            mobileNumber: formValues?.mobile?.number,
            pin: formValues?.pin?.pin,
            type: formValues.userType,
            fetchUsersData,
          });

        if (!isUndefined(updateUserKeyResponse)) {
          const updateUserResponse: CreateUserStatusResponse | null =
            updateUserKeyResponse && {
              statusCode: apiStatusCode.SUCCESS,
              data: updateUserKeyResponse,
            };

          console.log(`[UpdateUser] response `, updateUserResponse);
          if (updateUserResponse?.statusCode === apiStatusCode.SUCCESS) {
            console.log(`[UpdateUser] close modal `);

            props.handleCloseUserModal();
          }
        }
      } catch (error: unknown) {
        console.warn(`[UpdateUser]`, { error });
        throw error;
      } finally {
        console.warn(`[UpdateUser] finally block...`);
      }
    }
    console.log('Form submitted with values:', formValues);
  };

  const handleKebabClick = (clickUserDetailIndex: number): void => {
    setOpenMenuRowIndex((prevIndex: number | null) =>
      prevIndex === clickUserDetailIndex ? null : clickUserDetailIndex,
    );
  };

  const kebabOptions: Option[] = props.selectedUser
    ? getUserKebabOptions(
        props.selectedUser,
        props.loadingMenu,
        props.viewUserRowIndex,
      )
    : [];
  // We no longer need to filter fields based on dependencies
  // since we're now getting the exact fields needed for the selected user type
  // Filter fields based on dependencies
  const visibleFields: FieldConfig[] = createFieldConfigs.filter(
    (createFieldConfig: FieldConfig) => {
      if (!createFieldConfig.dependsOn) return true;

      const { field, value, condition } = createFieldConfig.dependsOn;
      const currentValue: any = formValues[field];

      if (condition === 'equals') return currentValue === value;
      if (condition === 'notEquals') return currentValue !== value;
      if (condition === 'contains') {
        return Array.isArray(value)
          ? value.includes(currentValue)
          : currentValue === value;
      }
      if (condition === 'greaterThan')
        return typeof currentValue === 'number' && currentValue > value;
      if (condition === 'lessThan')
        return typeof currentValue === 'number' && currentValue < value;

      return true;
    },
  );

  const handleFileSelect = async (file: File) => {
    const url = URL.createObjectURL(file);

    // Set initial photo alues
    handleChange('photo', {
      profilePicture: file,
      profilePictureUrl: url,
      fileName: file.name,
    });

    try {
      console.log(`[ENTRY][Convert FileToBase64] - ${file}`);
      const base64Image: string = await convertFileToBase64(file);
      handleChange('photo', {
        profilePictureBase64: base64Image,
        fileName: file.name,
      });
      console.log(`[FileToBase64] response `, base64Image);
    } catch (error) {
      console.error('Error converting file to base64:', error);
      handleChange('photo', {
        profilePictureBase64: null,
      });
    }
  };

  const handleFileDeselect = () => {
    const photo = formValues.photo;

    if (photo?.profilePictureUrl) {
      URL.revokeObjectURL(photo.profilePictureUrl);
    }

    handleChange('photo', {
      profilePicture: null,
      profilePictureUrl: null,
      profilePictureBase64: null,
      fileName: null,
    });
  };

  return (
    <ModalComponent
      open={props.isOpenUserModal}
      onToggle={props.handleCloseUserModal}
      shouldCloseOnOutsideClick={false}
      onFooterButton1Click={
        props.operation === UserOperation.VIEW
          ? props.handleEditUser
          : props.onCancel
      }
      onFooterButton2Click={
        props.operation === UserOperation.ADD ? onApply : onEdit
      }
      dialogClassName="top-[100px] w-[600px]"
      modalClassName="h-[700px]"
      title={renderModalTitle(props.operation)}
      footerButton1Text={
        props.operation === UserOperation.VIEW ? (
          <Trans i18nKey="userManagement.edit" />
        ) : props.operation === UserOperation.ADD ? (
          <Trans i18nKey="userManagement.cancel" />
        ) : (
          <></>
        )
      }
      showResendInvitation={
        props.selectedUser?.status === UserStatus.INVITED &&
        props.operation !== UserOperation.ADD
      }
      footerButton2Text={
        props.selectedUser?.status === UserStatus.INVITED &&
        props.operation !== UserOperation.ADD ? (
          <Trans i18nKey="userManagement.resendInvitation" />
        ) : props.operation === UserOperation.ADD ? (
          <Trans i18nKey="userManagement.add" />
        ) : props.operation === UserOperation.EDIT ? (
          <Trans i18nKey="userManagement.save" />
        ) : (
          <></>
        )
      }
      showFooter1Button={props.operation === UserOperation.VIEW}
      showFooter2Button={
        props.operation === UserOperation.ADD ||
        props.operation === UserOperation.EDIT
      }
      isShowClose
      onClose={props.handleCloseUserModal}
      activeLabel={props.activeLabel}
      setActiveLabel={props.setActiveLabel}
      showAction={props.operation === UserOperation.VIEW}
      showSwitch={
        props.operation !== UserOperation.ADD &&
        props.operation !== UserOperation.EDIT &&
        props.selectedUser?.status !== UserStatus.INVITED
      }
      kebabOptionUser={props.selectedUser}
      kebabOptions={kebabOptions}
      onKebabSelect={(action: string) => {
        if (props.selectedUser) {
          props.onKebabSelect(
            action,
            props.selectedUser,
            props.viewUserRowIndex,
          );
        }
      }}
      rowIndex={props.viewUserRowIndex}
      handleKebabClick={handleKebabClick}
      menuOpen={openMenuRowIndex === props.viewUserRowIndex}
      onChangeSwitch={props.onChangeSwitch}
      activeDeactiveLoading={props.activeDeactiveLoading}
      showCreateUserLoading={
        props.operation === UserOperation.ADD
          ? createUserLoading
          : inviteUpdateLoading
      }
    >
      {props.operation === UserOperation.VIEW ? (
        renderViewUser(userDetails)
      ) : props.operation === UserOperation.ADD ||
        props.operation === UserOperation.EDIT ? (
        <>
          {renderCreationForm(
            visibleFields,
            formValues,
            handleChange,
            errorMessage,
            t('onboarding.upload'),
            t('onboarding.supportedFormatsText'),
            handleFileSelect,
            handleFileDeselect,
          )}
          <div>
            {props.operation === UserOperation.EDIT && (
              <>
                {renderFormRow(
                  <Trans i18nKey="userManagement.userFilter.status" />,
                  <>
                    {' '}
                    {props.selectedUser?.status === UserStatus.INVITED ? (
                      <InviteButton className="bg-[#E7A702] py-1.5 w-[100px] rounded-full text-black font-medium hover:bg-[#E7A702] hover:text-black">
                        {'Invited'}
                      </InviteButton>
                    ) : (
                      <>
                        {props.activeDeactiveLoading ? (
                          <Loading height={30} width={30} />
                        ) : (
                          <Switch
                            activeLabel={props.activeLabel}
                            setActiveLabel={props.setActiveLabel}
                            onChangeSwitch={() => {
                              if (props.onChangeSwitch) {
                                props.onChangeSwitch(!props.activeLabel);
                              }
                            }}
                          />
                        )}
                      </>
                    )}
                  </>,
                )}
              </>
            )}
          </div>
        </>
      ) : (
        <></>
      )}
    </ModalComponent>
  );
};
